import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import Breadcrumbs from '../../components/BreadCrumbs';
import Card from '../../components/cards/Card';
import StyledHeadingText from '../../components/Fonts/StyledHeadingText';
import SearchBar from '../../components/searchBar/SearchBar';
import Dropdown from '../../components/inputFields/DropDown';
import DateRangePicker from '../../components/inputFields/DateRangePicker';
import { useLocation, Link } from 'react-router-dom';
import Button from '../../components/buttons/Button';
import Table from '../../components/tables/Table';
import Pagination from '../../components/tables/pagination/Pagination';
import CustomLoading from '../../components/customLoading/CustomLoading';
import { getOrders, getOrdersSummary, patchOrder } from '../../services/orders';
import { toast } from 'react-toastify';
import { BiCopy, BiRefresh } from 'react-icons/bi';
import ShowModal from '../../utils/ShowModal';
import { FaTruck } from 'react-icons/fa';
import TabView from '../../components/TabView';
import OrderOrdersDetailsPopView from './OrderOrdersDetailsPopView';
import AddShipment from './AddShipment';
import { GoEye } from 'react-icons/go';
import axios from 'axios';
import { getUserRole, isSalesStaffRole } from '../../helpers/local_storage_helpers';
import { canTransitionToDispatched } from '../../utils/orderLogistics';

function Orders({ orderType = 'B2C' }) {
    const location = useLocation();
    const ordersTabStorageKey = `ordersActiveTab_${orderType}`;
    const isStaffB2CView = orderType === 'B2C' && isSalesStaffRole();
    const isStaff = isSalesStaffRole();

    const ordersSectionName =
        orderType === 'B2B' ? 'B2B Orders' :
        orderType === 'THIRD_PARTY' ? 'Third Party Orders' :
        'Orders';

    const addOrderPath =
        orderType === 'B2B' ? '/B2B/orders/add' :
        orderType === 'THIRD_PARTY' ? '/third-party/orders/add' :
        '/orders/add';
    const [modalIsOrderDetailsOpen, setIsOrderDetailsOpen] = useState(false);
    const [modalIsLogisticsOpen, setIsLogisticsOpen] = useState(false);
    const [openingItem, setOpeningItem] = useState({});

    const [cardData, setCardData] = useState([]);

    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const headers = [
        { label: "ID", key: "id" },
        { label: "Date", key: "created_at" },
        { label: "Customer", key: "name" },
        { label: "Ord. St.", key: "status" },
        { label: "Sp. / Platform", key: "sp_platform" },
        { label: "Ord. Prev.", key: "order_preview" },
        { label: "Payment", key: "payment" },
        ...(isStaffB2CView ? [{ label: "Tracking", key: "remaining_payment" }] : []),
        { label: "Logi.", key: "logi" },
        { label: "View", key: "view" },
    ];

    const [searchText, setSearchText] = useState("");
    const [debouncedSearchText, setDebouncedSearchText] = useState("");
    const [filterStatus, setFilterStatus] = useState(() => {
        const stored = sessionStorage.getItem(ordersTabStorageKey);
        if (stored) return stored;
        if (orderType === 'B2C') return sessionStorage.getItem('ordersActiveTab') || '';
        return '';
    });
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    const todayStr = `${yyyy}-${mm}-${dd}`;
    const firstOfMonthStr = `${yyyy}-${mm}-01`;
    const [filterDate, setFilterDate] = useState({ start: firstOfMonthStr, end: todayStr });

    const pageSize = 10;
    const abortControllerRef = useRef(null);
    const summaryAbortControllerRef = useRef(null);
    const searchTimeoutRef = useRef(null);

    // Debounce search input (300ms delay)
    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        searchTimeoutRef.current = setTimeout(() => {
            setDebouncedSearchText(searchText);
        }, 300);

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchText]);

    // Centralized query builder function
    const buildQueryString = useCallback((overrides = {}) => {
        const params = new URLSearchParams();

        const search = overrides.search !== undefined ? overrides.search : debouncedSearchText;
        const status = overrides.status !== undefined ? overrides.status : filterStatus;
        const dateStart = overrides.start_date !== undefined ? overrides.start_date : filterDate.start;
        const dateEnd = overrides.end_date !== undefined ? overrides.end_date : filterDate.end;

        if (search) params.append('search', search);
        if (status) params.append('status', status);
        if (dateStart) params.append('start_date', dateStart);
        if (dateEnd) params.append('end_date', dateEnd);
        params.append('order_type', orderType);

        return `?${params.toString()}`;
    }, [debouncedSearchText, filterStatus, filterDate, orderType]);

    const handleSearch = useCallback((query) => {
        setSearchText(query);
        // Don't fetch immediately - let debounce handle it
    }, []);

    const handlePageChange = useCallback((page) => {
        setCurrentPage(page);
    }, []);

    const handleDateChange = useCallback((value) => {
        setFilterDate(value);
    }, []);

    const fetchData = useCallback(async (query) => {
        // Cancel previous request if it exists
        if (abortControllerRef.current) {
            abortControllerRef.current.abort('Operation canceled due to new request');
        }

        // Create new AbortController
        abortControllerRef.current = new AbortController();

        setLoading(true);
        try {
            let res = await getOrders(query, {
                signal: abortControllerRef.current.signal
            });
            if (res.status === 200) {
                const data = res.data;
                setData(data);
            } else {
                toast.error("Unable to fetch Data");
            }
        } catch (error) {
            if (axios.isCancel(error) || error.message === 'canceled') {
                console.log('Request canceled:', error.message);
            } else {
                console.error("Error in Orders:", error);
                toast.error("Unable to fetch Data");
            }
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchSummaryData = async () => {
        if (summaryAbortControllerRef.current) {
            summaryAbortControllerRef.current.abort('Operation canceled due to new request');
        }
        summaryAbortControllerRef.current = new AbortController();

        try {
            const params = new URLSearchParams();
            params.append('order_type', orderType);
            if (filterDate?.start) params.append('start_date', filterDate.start);
            if (filterDate?.end) params.append('end_date', filterDate.end);
            const queryParam = `?${params.toString()}`;
            let res = await getOrdersSummary(queryParam, {
                signal: summaryAbortControllerRef.current.signal
            });
            if (res.status === 200) {
                const data = res.data;
                // Backend now returns an array of objects: [{ title: "Total Orders", value: 343 }, ...]
                const formattedCards = data.map(item => ({
                    title: item.title,
                    count: item.value
                }));
                setCardData(formattedCards);
            } else {
                toast.error("Unable to fetch Data");
            }
        } catch (error) {
            if (axios.isCancel(error) || error.message === 'canceled') {
                console.log('Summary request canceled:', error.message);
            } else {
                console.error("Error in Orders:", error);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleTabChange = useCallback((value) => {
        setFilterStatus(value);
        sessionStorage.setItem(ordersTabStorageKey, value);
        if (orderType === 'B2C') {
            sessionStorage.setItem('ordersActiveTab', value);
        }
    }, [ordersTabStorageKey, orderType]);

    const openDetailsModal = (item) => {
        setOpeningItem(item);
        setIsOrderDetailsOpen(true);
    };

    // Close modal
    const closeDetailsModal = () => {
        setIsOrderDetailsOpen(false);
        setOpeningItem({});
    };

    const openLogisticsModal = (item) => {
        setOpeningItem(item);
        setIsLogisticsOpen(true);
    };

    const closeLogisticsModal = () => {
        setIsLogisticsOpen(false);
        setOpeningItem({});
    };

    const handleLogisticsSaved = useCallback(async () => {
        await fetchData(buildQueryString());
    }, [fetchData, buildQueryString]);

    const handleRowStatusChange = useCallback(async (item, value) => {
        const dispatchCheck = canTransitionToDispatched(item, value, { orderType });
        if (!dispatchCheck.allowed) {
            toast.error(dispatchCheck.message);
            return;
        }

        try {
            const data = {
                status: value,
            };
            let res = await patchOrder({
                data: data,
                id: item.id,
            });
            if (res.status === 200) {
                toast.success("Status Updated");
                fetchData(buildQueryString());
            } else {
                toast.error("Unable to Update Status");
            }
        } catch (error) {
            console.error("Error in Orders:", error);
            toast.error("Unable to Update Status");
        }
    }, [fetchData, buildQueryString]);

    const copyClipboard = (text) => {
        navigator.clipboard.writeText(text)
            .then(() => {
                toast.success('Copied to clipboard!');
            })
            .catch(() => {
                toast.error('Failed to copy address');
            });
    };

    const getPaidAmount = (item) => {
        const total = parseFloat(item?.total_price) || 0;
        const due = parseFloat(item?.remaining_payment) || 0;
        return Math.max(0, total - due);
    };

    const getLogisticsEntries = (item) => {
        if (!item) return [];
        if (Array.isArray(item.logistic)) return item.logistic;
        if (Array.isArray(item.logistics)) return item.logistics;
        if (item.logistics) return [item.logistics];
        return [];
    };

    /** Unique tracking URLs from order + all logistics rows. */
    const getTrackingLinksList = (item) => {
        const links = [];
        const push = (url) => {
            const u = String(url || "").trim();
            if (u && !links.includes(u)) links.push(u);
        };
        push(item?.tracking_link);
        getLogisticsEntries(item).forEach((entry) => push(entry?.tracking_link));
        return links;
    };

    const formatInr = (n) =>
        (Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const getSalesPersonDisplay = (order) => {
        if (!order) return null;
        if (order.sales_person_name) return order.sales_person_name;
        if (order.assigned_sales_person_name) return order.assigned_sales_person_name;
        if (order.staff_name) return order.staff_name;
        const sp = order.sales_person;
        if (sp == null || sp === '') return null;
        if (typeof sp === 'object') {
            return sp.name || sp.username || sp.email || null;
        }
        return String(sp);
    };

    const getSalesPersonInitials = (name) => {
        if (!name || !String(name).trim()) return '?';
        const parts = String(name).trim().split(/\s+/).filter(Boolean);
        if (parts.length >= 2) {
            return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
        }
        return parts[0].slice(0, 2).toUpperCase();
    };

    /** Salesperson initials in a circle; platform label beside it (single column). */
    const renderSalespersonPlatformCell = (item) => {
        const name = getSalesPersonDisplay(item);
        const platform = item?.platform;
        const initials = name ? getSalesPersonInitials(name) : '?';
        return (
            <div className="flex items-center gap-2 min-w-0">
                <span
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-200 text-[11px] font-semibold text-slate-700"
                    title={name || 'Salesperson'}
                >
                    {initials}
                </span>
                <span className="text-xs text-gray-900 truncate max-w-[7rem]" title={platform || ''}>
                    {platform || '—'}
                </span>
            </div>
        );
    };

    /** Total on top; paid (green) and due (red) side by side below — no labels. */
    const renderPaymentCell = (item) => {
        const total = parseFloat(item?.total_price) || 0;
        const paidAmount = getPaidAmount(item);
        const dueAmount = parseFloat(item?.remaining_payment) || 0;
        return (
            <div className="rounded-lg bg-gray-50 px-2 py-1.5 flex flex-col gap-0.5 text-left min-w-[5.5rem]">
                <span className="text-sm font-normal text-gray-900">₹{formatInr(total)}</span>
                <div className="flex flex-row items-baseline gap-2">
                    <span className="text-xs font-semibold text-green-700">₹{formatInr(paidAmount)}</span>
                    <span className="text-xs font-semibold text-red-600">₹{formatInr(dueAmount)}</span>
                </div>
            </div>
        );
    };

    /** Staff B2C: copy buttons only (one per tracking URL), no link text / WhatsApp. */
    const renderTrackingCopyCell = (item) => {
        const links = getTrackingLinksList(item);
        if (!links.length) {
            return <span className="text-gray-300 text-xs">—</span>;
        }
        return (
            <div className="flex flex-col items-center gap-1 py-0.5">
                {links.map((url, i) => (
                    <button
                        key={`${i}-${url.slice(0, 24)}`}
                        type="button"
                        onClick={() => copyClipboard(url)}
                        className="inline-flex items-center justify-center p-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-blue-600"
                        title={links.length > 1 ? `Copy tracking link ${i + 1}` : "Copy tracking link"}
                    >
                        <BiCopy size={18} />
                    </button>
                ))}
            </div>
        );
    };

    const renderLogiCell = (item) => (
        <div className="flex flex-col items-center justify-center py-0.5">
            <FaTruck
                size={20}
                className="text-blue-950 cursor-pointer"
                title="Logistics / shipment"
                onClick={() => openLogisticsModal(item)}
            />
        </div>
    );

    const renderCustomerCell = (item) => {
        const shopName = orderType === "B2B" ? String(item.shop_name || "").trim() : "";
        const thirdPartyOrderId =
            orderType === "THIRD_PARTY" ? String(item.third_party_order_id || "").trim() : "";
        const hasTopLine = Boolean(shopName || thirdPartyOrderId);
        return (
            <div className="flex flex-col leading-tight gap-0.5">
                {thirdPartyOrderId ? (
                    <span className="font-medium text-gray-900 truncate max-w-[12rem]" title={thirdPartyOrderId}>
                        {thirdPartyOrderId}
                    </span>
                ) : null}
                {shopName ? (
                    <span className="font-medium text-gray-900 truncate max-w-[12rem]" title={shopName}>
                        {shopName}
                    </span>
                ) : null}
                <span
                    className={`truncate max-w-[12rem] ${hasTopLine ? "text-xs text-gray-700" : "font-medium text-gray-900"}`}
                    title={item.user || undefined}
                >
                    {item.user || "—"}
                </span>
                <span className="text-xs text-gray-500">{item.phone_number || "—"}</span>
            </div>
        );
    };

    const getOrderItemsList = (item) => {
        if (Array.isArray(item?.order_items)) return item.order_items;
        if (Array.isArray(item?.items)) return item.items;
        return [];
    };

    const renderOrderPreviewCell = (item) => {
        const orderItems = getOrderItemsList(item);
        if (!orderItems.length) return <p className="text-xs text-gray-400">—</p>;

        const names = orderItems
            .map((it) => it?.aka_name || it?.product_aka_name || it?.product_name || it?.name || it?.product || "")
            .filter(Boolean);

        if (!names.length) return <p className="text-xs text-gray-400">—</p>;

        const maxLines = 3;
        const visible = names.slice(0, maxLines);
        const remaining = names.length - visible.length;

        return (
            <div className="max-w-[10rem] flex flex-col gap-0.5 leading-tight">
                {visible.map((name, idx) => (
                    <p key={idx} className="text-xs text-gray-800 truncate" title={name}>
                        {name}
                    </p>
                ))}
                {remaining > 0 && (
                    <p className="text-[10px] text-gray-500 font-medium">+{remaining} more</p>
                )}
            </div>
        );
    };

    const getOrderDisplayNumber = (item) =>
        item?.invoice_id ||
        item?.invoice_number ||
        item?.invoice_no ||
        item?.order_number ||
        item?.display_id ||
        item?.order_id ||
        item?.id;

    const renderOrderIdCell = (item) => (
        <span className='flex flex-row gap-1 items-center'>
            <p className="max-w-24 truncate cursor-help" title={getOrderDisplayNumber(item)}>
                {getOrderDisplayNumber(item)}
            </p>
            <BiCopy
                className='cursor-pointer'
                color='blue'
                title="Copy UUID"
                onClick={() => { copyClipboard(item.id); }}
            />
        </span>
    );

    const resetFilters = useCallback(() => {
        setSearchText("");
        setDebouncedSearchText("");
        setFilterDate({ start: null, end: null });
        setFilterStatus("");
    }, []);


    // Fetch data when filters change
    useEffect(() => {
        const query = buildQueryString();
        fetchData(query);

        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort('Component unmounted');
            }
        };
    }, [debouncedSearchText, filterStatus, filterDate.start, filterDate.end, orderType, buildQueryString, fetchData]);

    // Fetch summary data on mount and when date/platform changes
    useEffect(() => {
        fetchSummaryData();
        return () => {
            if (summaryAbortControllerRef.current) {
                summaryAbortControllerRef.current.abort('Component unmounted');
            }
        };
    }, [location.pathname, orderType, filterDate.start, filterDate.end]);

    // Reset to first page when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearchText, filterStatus, filterDate.start, filterDate.end]);

    const getStatusColor = (status) => {
        switch (status) {
            case 'PENDING':      return '#f59e0b';
            case 'CONFIRMED':    return '#3b82f6';
            case 'IN_PACKAGING':        return '#8b5cf6';
            case 'READY_TO_DISPATCH':   return '#06b6d4';
            case 'DISPATCHED':          return '#f97316';
            case 'DELIVERED':    return '#22c55e';
            case 'CANCELLED':    return '#ef4444';
            case 'RETURNED':     return '#9ca3af';
            default:             return '#ffffff';
        }
    };

    const renderStatusDropdown = (item) => (
        <Dropdown
            name="status"
            color={getStatusColor(item.status)}
            options={[
                { label: "Pending", value: "PENDING" },
                { label: "Confirmed", value: "CONFIRMED" },
                { label: "Packaging", value: "IN_PACKAGING" },
                { label: "Ready to Dispatch", value: "READY_TO_DISPATCH" },
                { label: "Dispatched", value: "DISPATCHED" },
                { label: "Delivered", value: "DELIVERED" },
                { label: "Cancelled", value: "CANCELLED" },
                { label: "Returned", value: "RETURNED" },
            ]}
            placeholder={item.status?.replace(/_/g, ' ')}
            value={item.status}
            disabled={isStaff}
            onChange={(value) => handleRowStatusChange(item, value)}
        />
    );

    const buildOrderRow = (item) => ({
        id: renderOrderIdCell(item),
        created_at: <p>{item.order_date}</p>,
        name: renderCustomerCell(item),
        status: renderStatusDropdown(item),
        sp_platform: renderSalespersonPlatformCell(item),
        order_preview: renderOrderPreviewCell(item),
        payment: renderPaymentCell(item),
        ...(isStaffB2CView ? { remaining_payment: renderTrackingCopyCell(item) } : {}),
        logi: renderLogiCell(item),
        view: (
            <div className="flex flex-row justify-center">
                <GoEye size={24} color="purple" className="cursor-pointer" onClick={() => { openDetailsModal(item); }} />
            </div>
        ),
    });

    const summaryByTitle = useMemo(() => {
        const map = {};
        cardData.forEach((card) => {
            map[card.title] = card;
        });
        return map;
    }, [cardData]);

    const renderSummaryCard = (title, bgGradient = "from-white to-primary/20", textColor = "text-black") => {
        const card = summaryByTitle[title];
        if (!card) return null;
        return <Card key={title} title={card.title} count={card.count} bgGradient={bgGradient} textColor={textColor} />;
    };

    return (<>
        <Breadcrumbs items={[
            { name: 'Home', link: '/dashboard' },
            { name: ordersSectionName },
        ]} />
        <div className="py-3">
            <StyledHeadingText text={ordersSectionName} />
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                {/* Orders */}
                <div className="rounded-xl border border-blue-200 bg-gradient-to-r from-blue-50 to-blue-100 p-3">
                    <p className="text-xs text-blue-700 font-semibold uppercase tracking-wide">Orders</p>
                    <p className="text-2xl font-bold text-blue-900">{summaryByTitle["Total Orders"]?.count ?? "--"}</p>
                    <p className="text-xs text-blue-700 mt-1">Avg/Day: {summaryByTitle["Average Order / Day"]?.count ?? "--"}</p>
                </div>
                {/* Ready to Dispatch */}
                <div className="rounded-xl border-2 border-orange-400 bg-gradient-to-r from-orange-100 to-amber-100 p-3 shadow-sm">
                    <p className="text-xs text-orange-800 font-semibold uppercase tracking-wide">Ready to Dispatch</p>
                    <p className="text-3xl font-extrabold text-orange-900 leading-none mt-1">{summaryByTitle["Ready to Dispatch"]?.count ?? "--"}</p>
                    <p className="text-xs text-orange-800 mt-1">
                        Confirmed: {summaryByTitle["Confirmed"]?.count ?? "--"} &nbsp;|&nbsp; Packing: {summaryByTitle["In Packaging"]?.count ?? "--"}
                    </p>
                </div>
                {/* Sales */}
                <div className="rounded-xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-emerald-100 p-3">
                    <p className="text-xs text-emerald-700 font-semibold uppercase tracking-wide">Sales</p>
                    <p className="text-xl font-bold text-emerald-900">{summaryByTitle["Total Sales"]?.count ?? "--"}</p>
                    <p className="text-xs text-emerald-700 mt-1">AOV: {summaryByTitle["Average Order Value"]?.count ?? "--"}</p>
                </div>
                {/* Dispatched / Delivered */}
                <div className="rounded-xl border border-sky-200 bg-gradient-to-r from-sky-50 to-blue-100 p-3">
                    <p className="text-xs text-sky-700 font-semibold uppercase tracking-wide">Flow</p>
                    <p className="text-sm font-bold text-sky-900">Dispatched: <span className="text-xl">{summaryByTitle["Dispatched"]?.count ?? "--"}</span></p>
                    <p className="text-sm font-bold text-green-800 mt-1">Delivered: <span className="text-xl">{summaryByTitle["Delivered"]?.count ?? "--"}</span></p>
                </div>
                {/* Returns */}
                <div className="rounded-xl border border-rose-200 bg-gradient-to-r from-rose-50 to-rose-100 p-3">
                    <p className="text-xs text-rose-700 font-semibold uppercase tracking-wide">Returns</p>
                    <p className="text-sm font-bold text-rose-900">Count: <span className="text-xl">{summaryByTitle["Returned & Cancelled"]?.count ?? "--"}</span></p>
                    <p className="text-xs text-rose-700 mt-1">{summaryByTitle["Cancelled & Returned Amount"]?.count ?? "--"}</p>
                </div>
                {/* Delayed Alert */}
                {summaryByTitle["⚠️ Delayed Order Alert"] && (
                    <div className={`rounded-xl border-2 p-3 ${summaryByTitle["⚠️ Delayed Order Alert"]?.count > 0 ? "border-red-400 bg-gradient-to-r from-red-50 to-red-100" : "border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100"}`}>
                        <p className={`text-xs font-semibold uppercase tracking-wide ${summaryByTitle["⚠️ Delayed Order Alert"]?.count > 0 ? "text-red-700" : "text-gray-500"}`}>Delayed</p>
                        <p className={`text-3xl font-extrabold leading-none mt-1 ${summaryByTitle["⚠️ Delayed Order Alert"]?.count > 0 ? "text-red-900" : "text-gray-400"}`}>
                            {summaryByTitle["⚠️ Delayed Order Alert"]?.count ?? "--"}
                        </p>
                        {summaryByTitle["⚠️ Delayed Order Alert"]?.count > 0 && (
                            <p className="text-xs text-red-700 mt-1">Orders overdue!</p>
                        )}
                    </div>
                )}
            </div>
        </div>
        <div className="flex flex-row items-center justify-between gap-3 flex-wrap">
            <div className="flex flex-row items-center gap-2 flex-1 min-w-0">
                <SearchBar placeholder="Search for Order ID/ Farmer Name" onSearch={handleSearch} value={searchText} />
                <DateRangePicker
                    value={filterDate}
                    onChange={handleDateChange}
                    placeholder="Date Range"
                />
                {(searchText.length !== 0 || filterDate.start !== null || filterDate.end !== null) &&
                    <BiRefresh size={24} className='cursor-pointer text-primary shrink-0' onClick={resetFilters} />
                }
            </div>
            <Link to={addOrderPath} className='shrink-0'>
                <Button
                    type="button"
                    className="hover:bg-black hover:opacity-70 text-white bg-black rounded-md"
                >
                    Add Order
                </Button>
            </Link>
        </div>
        <TabView
            onChanged={(val) => handleTabChange(val)}
            isCenterAligned={false}
            tabs={[
                {
                    key: 'all',
                    title: 'All',
                    content: (
                        <>
                            {
                                loading ?
                                    <div className="flex w-full flex-row justify-center my-2">
                                        <CustomLoading size={100} />
                                    </div>
                                    :
                                    <div className="mt-2">
                                        <Table
                                            headers={headers}
                                            rows={data?.results?.map((item) => buildOrderRow(item))}
                                        />
                                    </div>}
                            {/* Pagination */}
                            {
                                data.count !== 0 ?
                                    <Pagination
                                        currentPage={currentPage}
                                        pageSize={pageSize}
                                        totalEntries={data?.count}
                                        onPageChange={handlePageChange}
                                        data={data}
                                        setData={setData}
                                    /> : <></>
                            }</>
                    ),
                },
                {
                    key: 'CONFIRMED',
                    title: 'Confirmed',
                    content: (
                        <>
                            {
                                loading ?
                                    <div className="flex w-full flex-row justify-center my-2">
                                        <CustomLoading size={100} />
                                    </div>
                                    :
                                    <div className="mt-2">
                                        <Table
                                            headers={headers}
                                            rows={data?.results?.map((item) => buildOrderRow(item))}
                                        />
                                    </div>}
                            {/* Table End */}
                            {/* Pagination Begin */}
                            {
                                data.count !== 0 ?
                                    <Pagination
                                        currentPage={currentPage}
                                        pageSize={pageSize}
                                        totalEntries={data?.count}
                                        onPageChange={handlePageChange}
                                        data={data}
                                        setData={setData}
                                    /> : <></>
                            }</>
                    ),
                },
                {
                    key: 'IN_PACKAGING',
                    title: 'Packing',
                    content: (
                        <>
                            {
                                loading ?
                                    <div className="flex w-full flex-row justify-center my-2">
                                        <CustomLoading size={100} />
                                    </div>
                                    :
                                    <div className="mt-2">
                                        <Table
                                            headers={headers}
                                            rows={data?.results?.map((item) => buildOrderRow(item))}
                                        />
                                    </div>}
                            {
                                data.count !== 0 ?
                                    <Pagination
                                        currentPage={currentPage}
                                        pageSize={pageSize}
                                        totalEntries={data?.count}
                                        onPageChange={handlePageChange}
                                        data={data}
                                        setData={setData}
                                    /> : <></>
                            }</>
                    ),
                },
                {
                    key: 'READY_TO_DISPATCH',
                    title: 'Ready to Dispatch',
                    content: (
                        <>
                            {
                                loading ?
                                    <div className="flex w-full flex-row justify-center my-2">
                                        <CustomLoading size={100} />
                                    </div>
                                    :
                                    <div className="mt-2">
                                        <Table
                                            headers={headers}
                                            rows={data?.results?.map((item) => buildOrderRow(item))}
                                        />
                                    </div>}
                            {
                                data.count !== 0 ?
                                    <Pagination
                                        currentPage={currentPage}
                                        pageSize={pageSize}
                                        totalEntries={data?.count}
                                        onPageChange={handlePageChange}
                                        data={data}
                                        setData={setData}
                                    /> : <></>
                            }</>
                    ),
                },
                {
                    key: 'DISPATCHED',
                    title: 'Dispatched',
                    content: (
                        <>
                            {
                                loading ?
                                    <div className="flex w-full flex-row justify-center my-2">
                                        <CustomLoading size={100} />
                                    </div>
                                    :
                                    <div className="mt-2">
                                        <Table
                                            headers={headers}
                                            rows={data?.results?.map((item) => buildOrderRow(item))}
                                        />
                                    </div>}
                            {/* Table End */}
                            {/* Pagination Begin */}
                            {
                                data.count !== 0 ?
                                    <Pagination
                                        currentPage={currentPage}

                                        pageSize={pageSize}
                                        totalEntries={data?.count}
                                        onPageChange={handlePageChange}
                                        data={data}
                                        setData={setData}
                                    /> : <></>
                            }</>
                    ),
                },
                {
                    key: 'DELIVERED',
                    title: 'Delivered',
                    content: (
                        <>
                            {
                                loading ?
                                    <div className="flex w-full flex-row justify-center my-2">
                                        <CustomLoading size={100} />
                                    </div>
                                    :
                                    <div className="mt-2">
                                        <Table
                                            headers={headers}
                                            rows={data?.results?.map((item) => buildOrderRow(item))}
                                        />
                                    </div>}
                            {/* Table End */}
                            {/* Pagination Begin */}
                            {
                                data.count !== 0 ?
                                    <Pagination
                                        currentPage={currentPage}

                                        pageSize={pageSize}
                                        totalEntries={data?.count}
                                        onPageChange={handlePageChange}
                                        data={data}
                                        setData={setData}
                                    /> : <></>
                            }</>
                    ),
                },
                {
                    key: 'CANCELLED',
                    title: 'Cancelled',
                    content: (
                        <>
                            {
                                loading ?
                                    <div className="flex w-full flex-row justify-center my-2">
                                        <CustomLoading size={100} />
                                    </div>
                                    :
                                    <div className="mt-2">
                                        <Table
                                            headers={headers}
                                            rows={data?.results?.map((item) => buildOrderRow(item))}
                                        />
                                    </div>}
                            {/* Table End */}
                            {/* Pagination Begin */}
                            {
                                data.count !== 0 ?
                                    <Pagination
                                        currentPage={currentPage}

                                        pageSize={pageSize}
                                        totalEntries={data?.count}
                                        onPageChange={handlePageChange}
                                        data={data}
                                        setData={setData}
                                    /> : <></>
                            }</>
                    ),
                },
                {
                    key: 'RETURNED',
                    title: 'Returned',
                    content: (
                        <>
                            {
                                loading ?
                                    <div className="flex w-full flex-row justify-center my-2">
                                        <CustomLoading size={100} />
                                    </div>
                                    :
                                    <div className="mt-2">
                                        <Table
                                            headers={headers}
                                            rows={data?.results?.map((item) => buildOrderRow(item))}
                                        />
                                    </div>}
                            {/* Table End */}
                            {/* Pagination Begin */}
                            {
                                data.count !== 0 ?
                                    <Pagination
                                        currentPage={currentPage}

                                        pageSize={pageSize}
                                        totalEntries={data?.count}
                                        onPageChange={handlePageChange}
                                        data={data}
                                        setData={setData}
                                    /> : <></>
                            }</>
                    ),
                },
            ]} />
        <ShowModal contentLabel={"Order Details alert"} isOpen={modalIsOrderDetailsOpen} modalWidth="50%" isSidebar={true} onRequestClose={closeDetailsModal} componentToPass={<OrderOrdersDetailsPopView item={openingItem} onListRefresh={() => fetchData(buildQueryString())} />} />
        <ShowModal
            contentLabel={"Logistics"}
            isOpen={modalIsLogisticsOpen}
            modalWidth="75%"
            isSidebar={true}
            onRequestClose={closeLogisticsModal}
            componentToPass={
                <AddShipment
                    orderData={{ ...openingItem, fromPage: 'orders' }}
                    onClose={closeLogisticsModal}
                    onSaved={handleLogisticsSaved}
                    isDrawerMode={true}
                />
            }
        />
    </>);
}

export default Orders;